import { useTheme } from "../ThemeProvider";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";
import usePermiso from "../../hooks/usePermiso";

const BTN = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "inherit",
};

/**
 * Overlay con pointer-events: none para poder marcar más filas en la tabla sin cerrar el modal.
 * Cierre solo con ✕ (y al elegir una acción el padre cierra).
 */
export default function ModalAccionesSeleccionPropiedades({ isOpen, onClose, count, onAction, carteraFiltro }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const { puede } = usePermiso();
  const puedeEliminar = puede("propiedades", "baja");
  const puedeVerMasivaEliminarDef = puede("prop_masiva_eliminar_def", "ver");
  const puedeVerMasivaDevolverActiva = puede("prop_masiva_devolver_activa", "ver");
  const puedeVerMasivaWhatsapp = puede("prop_masiva_whatsapp", "ver");
  const puedeVerMasivaEmail = puede("prop_masiva_email", "ver");
  const puedeVerMasivaLocalizacion = puede("prop_masiva_localizacion", "ver");
  const puedeVerMasivaImprimir = puede("prop_masiva_imprimir", "ver");
  const puedeVerMasivaVendida = puede("prop_masiva_vendida", "ver");
  const puedeModificarMasivaVendida = puede("prop_masiva_vendida", "modificar");
  const puedeVerMasivaReservada = puede("prop_masiva_reservada", "ver");
  const puedeModificarMasivaReservada = puede("prop_masiva_reservada", "modificar");
  const puedeVerMasivaInteresados = puede("prop_masiva_interesados", "ver");
  const puedeVerMasivaOcultar = puede("prop_masiva_ocultar", "ver");
  const puedeModificarMasivaOcultar = puede("prop_masiva_ocultar", "modificar");
  const puedeVerMasivaArchivar = puede("prop_masiva_archivar", "ver");

  if (!isOpen) return null;

  // Acciones especiales/contextuales: se muestran a ancho completo, encima de la cuadrícula.
  const specialItems = [
    ...(carteraFiltro === "eliminadas" && puedeEliminar && puedeVerMasivaEliminarDef
      ? [
          {
            id: "eliminar_definitiva",
            emoji: "⛔",
            label: "Eliminar definitivamente",
            sub: "Borra la ficha, fotos, documentos y datos relacionados · irreversible",
            danger: true,
          },
        ]
      : []),
    ...(carteraFiltro !== "activas" && carteraFiltro !== "alquiler" && puedeVerMasivaDevolverActiva
      ? [
          {
            id: "devolver_activa",
            emoji: "↩️",
            label: "Devolver a activa",
            sub: "Disponible y publicada en web · quita cualquier restricción",
            danger: false,
          },
        ]
      : []),
  ];

  // Iconos de WhatsApp y email iguales a los de "Acciones rápidas" de la ficha de contacto (más representativos que el emoji genérico).
  const iconWhatsapp = (
    <div style={{ width: 28, height: 28, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", background: c.btnWhatsappBg || "linear-gradient(135deg, #25d366, #128c7e)", boxShadow: c.btnWhatsappShadow || "0 2px 6px rgba(37,211,102,0.3)", flexShrink: 0 }}>
      <svg width={15} height={15} viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" /></svg>
    </div>
  );
  const iconEmail = (
    <div style={{ width: 28, height: 28, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", background: c.btnEmailBg || "#b45309", boxShadow: c.btnEmailShadow || "0 2px 6px rgba(180,83,9,0.25)", flexShrink: 0 }}>
      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>
    </div>
  );

  // Acciones regulares: se muestran en cuadrícula de 2 columnas, en este orden fijo.
  const items = [
    ...(puedeVerMasivaWhatsapp ? [{ id: "whatsapp", icon: iconWhatsapp, label: "Enviar por WhatsApp", sub: "Destinatario manual" }] : []),
    ...(puedeVerMasivaEmail ? [{ id: "email", icon: iconEmail, label: "Enviar por email", sub: "Correo manual" }] : []),
    ...(puedeVerMasivaLocalizacion ? [{ id: "localizacion", emoji: "📍", label: "Enviar localización", sub: "WhatsApp con enlace a maps" }] : []),
    { id: "hoja_visitas", emoji: "📋", label: "Generar hoja de visitas", sub: "Próximamente" },
    ...(puedeVerMasivaVendida
      ? [{
          id: "vendida",
          emoji: "✅",
          label: "Marcar como vendida/alquilada",
          sub: puedeModificarMasivaVendida ? "No disponible · sin publicar web" : "No tienes permiso para modificar",
          danger: false,
          disabled: !puedeModificarMasivaVendida,
        }]
      : []),
    ...(puedeVerMasivaReservada
      ? [{
          id: "reservada",
          emoji: "🔖",
          label: "Marcar como reservada",
          sub: puedeModificarMasivaReservada ? "No disponible" : "No tienes permiso para modificar",
          disabled: !puedeModificarMasivaReservada,
        }]
      : []),
    ...(puedeVerMasivaOcultar
      ? [{
          id: "ocultar",
          emoji: "🙈",
          label: "Ocultar",
          sub: puedeModificarMasivaOcultar ? "Embudo frío + sin web" : "No tienes permiso para modificar",
          disabled: !puedeModificarMasivaOcultar,
        }]
      : []),
    ...(puedeVerMasivaInteresados ? [{ id: "interesados", emoji: "👥", label: "Ver contactos interesados", sub: "Una propiedad" }] : []),
    ...(carteraFiltro !== "eliminadas" && puedeEliminar && puedeVerMasivaArchivar
      ? [{ id: "eliminar", emoji: "🗑️", label: "Archivar", sub: "Pasa a Eliminadas; se puede restaurar después", danger: true }]
      : []),
    ...(puedeVerMasivaImprimir ? [{ id: "imprimir", emoji: "🖨️", label: "Imprimir", sub: "Opciones en definición" }] : []),
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1050,
        pointerEvents: "none",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          pointerEvents: "none",
        }}
      >
        <div
          ref={dragRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: "auto",
            width: 560,
            maxWidth: "100%",
            maxHeight: "90vh",
            overflow: "auto",
            background: theme.name === 'dark' ? c.bgCard : '#c8c8be',
            borderRadius: 16,
            border: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`,
            boxShadow: theme.name === 'dark' ? "0 20px 50px rgba(0,0,0,0.25)" : "0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)",
            ...panelPositionStyle,
          }}
        >
          <div
            onMouseDown={startDrag}
            style={{
              padding: "14px 20px",
              borderBottom: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`,
              background: theme.name === 'dark' ? 'transparent' : '#e0e0d8',
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              cursor: "grab",
              userSelect: "none",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Acciones</h2>
              <p style={{ margin: "6px 0 0", fontSize: 12, color: c.textMuted }}>
                {count} propiedad{count !== 1 ? "es" : ""} seleccionada{count !== 1 ? "s" : ""}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 10, color: c.textMuted, opacity: 0.9 }}>
                Arrastra la barra para mover · Puedes seguir marcando filas en la tabla
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: c.bgInput,
                color: c.textMuted,
                cursor: "pointer",
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {specialItems.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {specialItems.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    disabled={it.disabled}
                    onClick={() => { if (!it.disabled) onAction(it.id); }}
                    title={it.disabled ? "No tienes permiso para modificar" : undefined}
                    style={{
                      ...BTN,
                      borderColor: it.danger ? "rgba(239,68,68,0.45)" : c.border,
                      background: it.danger ? "rgba(239,68,68,0.06)" : c.bgInput,
                      color: it.danger ? "#ef4444" : c.textPrimary,
                      cursor: it.disabled ? "not-allowed" : "pointer",
                      opacity: it.disabled ? 0.5 : 1,
                    }}
                  >
                    <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>{it.emoji}</span>
                    <span style={{ flex: 1 }}>
                      <span style={{ display: "block" }}>{it.label}</span>
                      <span style={{ display: "block", fontSize: 10, fontWeight: 500, opacity: 0.75, marginTop: 2 }}>{it.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  disabled={it.disabled}
                  onClick={() => { if (!it.disabled) onAction(it.id); }}
                  title={it.disabled ? "No tienes permiso para modificar" : undefined}
                  style={{
                    ...BTN,
                    borderColor: it.danger ? "rgba(239,68,68,0.45)" : c.border,
                    background: it.danger ? "rgba(239,68,68,0.06)" : c.bgInput,
                    color: it.danger ? "#ef4444" : c.textPrimary,
                    cursor: it.disabled ? "not-allowed" : "pointer",
                    opacity: it.disabled ? 0.5 : 1,
                  }}
                >
                  {it.icon || <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>{it.emoji}</span>}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block" }}>{it.label}</span>
                    <span style={{ display: "block", fontSize: 10, fontWeight: 500, opacity: 0.75, marginTop: 2 }}>{it.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
